// 3rd party
import React, { ReactElement, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Components
import { Button, MachinePartsTable } from 'components';

// Constants
import { sfEnabled } from 'constants/featureFlags';

// Types
import { Filter, SortState, User } from 'types';
import { TableRow, Part, PartDataWithKey, Product, SavedPart } from 'types/parts';

// API
import { 
  useAddProductToCartMutation, 
  useDeleteSavedProductMutation, 
  useAddProductToRemoteCartMutation 
} from 'api';

// Custom hooks
import { useFilter, useSearch, useSort } from 'hooks';

// State management
import { cartActions } from 'actions';
import { useCart, useUser } from 'selectors';

// Constants
import { AnalyticsCategories, ECommerceAnalyticEventActions } from 'constants/analytics';

// Helpers
import { generateAnalyticsEvent } from 'common/helpers/v1/analytics';
import { CartItem, CartState } from 'types/parts/cart';
import { ToastMsg } from 'common/components/Toast';
import ShoppingCartAdd from 'icons/indexed-icons/ShoppingCartAdd';
import theme from 'themes';
import { isPriceMissing } from 'components/CustomerPrice';
import { ComposeCartItem } from 'components/MachinePartsTable/CartHelp';

/* Interfaces used for styling and local objects */
interface SavedProductTableProps {
  data: SavedPart[];
  onRefresh: CallableFunction;
  title?: string;
  showHeader?: boolean;
  isDataLoading?: boolean;
  showSearch?: boolean;
  machineId?: string;
  handleShowModal?: () => void;
}

/* End interfaces */

/* Styling */
const Root = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const ModalFooter = styled.div`
  display: flex;
  padding: 1rem;
  justify-content: end;
  gap: 1rem;
  .add-cart-btn {
    gap: 0.5rem;
    align-items: center;
    padding: 0.3rem 1rem;
  }
  .icon--shopping-cart-add {
    width: 1rem;
  }
`;

const SavedProductsTableContainer = styled.div`
  padding: 0 2rem;
  flex: 1;
  .parts-table {
    flex: 1;
    max-height: calc(100vh - 28rem);
    .product-desc {
      max-width: 10rem;
    }
    .gear-icon {
      margin-top: 1rem;
    }
    .data-not-found-text {
      margin-bottom: 0;
    }
  }
`;

export const RemoveFromProductsIcon = styled.div`
  display: block;
  text-align: center;
  align-items: center;
  min-width: 10%;
  max-width: 10rem;
  cursor: pointer;
`;

/* End styling */

const addKeysToPartData = (keyPrefix: string, data: Part[]): PartDataWithKey[] => {
  return data.map((item) => {
    return {
      ...item,
      key: `${keyPrefix}-${item.sku}`
    };
  });
};

const generateAlternateParts = (partRows: PartDataWithKey[]): TableRow[] => {
  const rows: TableRow[] = [];
  partRows.forEach((partRow) => {
    if (partRow.alternateSku && partRow.alternateProduct) {
      rows.push({
        rowType: 'alternate',
        ...partRow
      });
    }
    rows.push(partRow);
  });
  return rows;
};

/* End helper functions */

/* Initial states for sorting and filtering */
const defaultSortState: Record<string, SortState> = {
  label: SortState.unsorted,
  sku: SortState.unsorted,
  leadTime: SortState.unsorted
};

const defaultFilterState = [{ property: 'partTypes', value: '' }];

const searchByProps = ['description', 'sku'];
/* End initial states */

const SavedProductTable = ({
  data,
  onRefresh,
  isDataLoading,
  showSearch = true,
  machineId = undefined,
  handleShowModal
}: SavedProductTableProps): ReactElement => {
  const dispatch = useDispatch();
  const [deleteProduct] = useDeleteSavedProductMutation();

  const keyPrefix = 'm-parts';
  const [searchVal] = useState<string>('');
  const [sortState] = useState<Record<string, SortState>>(defaultSortState);
  const [filters] = useState<Filter[]>(defaultFilterState);
  const [addProductToCart] = useAddProductToCartMutation();
  const [AddProductToRemoteCart] = useAddProductToRemoteCartMutation();
  const user = useUser() as User;
  const cart = useCart() as CartState;
  const { t } = useTranslation(['fpns']);

  const searchedData = useSearch<SavedPart>(searchVal, data, searchByProps);
  const filteredData = useFilter<SavedPart>(filters, showSearch ? searchedData : data);
  const sortedData = useSort<Part>(sortState, filteredData);

  const dataWithKeys: PartDataWithKey[] = addKeysToPartData(keyPrefix, sortedData);
  const dataWithAlternates: TableRow[] = generateAlternateParts(dataWithKeys);

  const addToCartHandler = (product: Product) => {
    product.isPriceMissing = product.isPriceMissing || isPriceMissing(product);
    try {
      if (sfEnabled()) {
        addProductToCart({
          accountId: user.plants[0],
          cart: cart,
          product: product,
          productId: product.sfProductId as string,
          quantity: 1,
          machineId: machineId
        });
      } else {
        AddProductToRemoteCart([ComposeCartItem(product, machineId as string, '')] as CartItem[])
            .unwrap()
            .then(() => {
              // maintain temporay FE cartState to track cart count
              dispatch({
                  type: cartActions.ADD_TO_CART,
                  item: product,
                  quantity: product.quantity,
                  groupId: machineId,
                  hideToast: true
              });
            })
            .catch((error) => {
                console.warn('Error adding item to cart: ', error);
            });
      }
      generateAnalyticsEvent({
        category: AnalyticsCategories.ECOMMERCE,
        action: ECommerceAnalyticEventActions.ADD_TO_CART_FROM_SEARCH_RESULTS,
        event: ECommerceAnalyticEventActions.ADD_TO_CART,
        currency: product.priceUnit,
        value: product.custPrice,
        items: [
          {
            item_id: product.id,
            item_name: product.description,
            quantity: product.stock,
            price: product.custPrice,
            currency: product.priceUnit
          }
        ]
      });
    } catch (error) {
      console.warn('Add to cart failed! Error:', error);
      ToastMsg({
        heading: 'Warning',
        message: `There was a problem adding ${product.sku} to the cart`,
        type: 'warning'
      });
    }
  };

  const handleRemovePart = (data: Product, hideToast?: boolean) => {
    const { id, description, priceUnit } = data;
    deleteProduct({
      productId: id,
      priceUnit: priceUnit
    })
      .unwrap()
      .then(() => {
        onRefresh();
        if (!hideToast) {
          ToastMsg({
            heading: 'Success',
            type: 'success',
            message: description + t('saved_product_remove')
          });
        }
      })
      .catch((error) => {
        console.warn('Delete saved product error: ', error);
        if (error.status !== 404) {
          ToastMsg({
            heading: 'Error',
            message: t('saved_product_delete_error'),
            type: 'error'
          });
        }
      });
  };
  const handleAddToCart = () => {
    dataWithKeys.map((product) => {
      addToCartHandler(product);
      handleRemovePart(product, true);
    });
    ToastMsg({
      heading: 'Success!',
      message: 'Product(s) added to cart',
      type: 'info',
      position: 'top-right'
    });
  };
  return (
    <Root className="root">
      <SavedProductsTableContainer>
        <MachinePartsTable
          showSearch={false}
          showHeader={false}
          data={(dataWithAlternates as Part[]) || []}
          isSavedPartsTable
          handleRemovePart={handleRemovePart}
          isDataLoading={isDataLoading}
        />
      </SavedProductsTableContainer>
      <ModalFooter>
        <Button variant="outline-blue" onClick={handleShowModal} width="107px" size="small">
          {t('close')}
        </Button>
        <Button
          variant="filled-primary"
          // width="114px"
          onClick={handleAddToCart}
          size="small"
          disabled={!dataWithAlternates.length}
          className="add-cart-btn"
        >
          <ShoppingCartAdd color={theme.colors.white} />
          {t('add_all_to_cart') as string}
        </Button>
      </ModalFooter>
    </Root>
  );
};

export default SavedProductTable;
